import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { buyMembership } from '../../api/rsa.api';
import '../../styles/user-dashboard.css';

const PAYMENT_METHODS = [
  { id: 'upi', label: 'UPI / QR Pay', desc: 'Google Pay, PhonePe, Paytm', icon: '📱' },
  { id: 'card', label: 'Credit / Debit Card', desc: 'Visa, Mastercard, RuPay', icon: '💳' },
  { id: 'netbanking', label: 'Net Banking', desc: 'All major Indian banks', icon: '🏦' },
  { id: 'cash', label: 'Cash on Activation', desc: 'Pay our agent on visit', icon: '💵' },
];

const Payment = () => {
  const location = useLocation();
  const state = location.state;

  const [method, setMethod] = useState('upi');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  if (!state?.amount) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: '#64748b' }}>No payment details found.</p>
        <Link to="/rsa-join" style={{ color: '#2563eb', fontWeight: 600 }}>Go to RSA Plans</Link>
      </div>
    );
  }

  const handlePay = async () => {
    setError('');
    setLoading(true);
    try {
      if (state.type === 'rsa') {
        const today = new Date();
        const expiry = new Date(today);
        expiry.setFullYear(expiry.getFullYear() + 1);

        const payload = {
          ...state.memberDetails,
          startDate: today.toISOString().split('T')[0],
          expiryDate: expiry.toISOString().split('T')[0],
          paymentStatus: method === 'cash' ? 'pending' : 'paid',
        };

        const data = await buyMembership(payload);
        setSuccess({
          type: 'rsa',
          membershipId: data.membershipId || data.member?.membershipId,
          planName: state.planName,
          amount: state.amount,
          paymentMethod: PAYMENT_METHODS.find((m) => m.id === method)?.label,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-card">
            <div className="payment-success">
              <div className="payment-success-icon">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="payment-success-title">
                {method === 'cash' ? 'Request Confirmed!' : 'Payment Successful!'}
              </div>
              <div className="payment-success-detail">
                {method === 'cash'
                  ? 'Our agent will contact you to collect payment and activate membership.'
                  : `Payment of ₹${Number(state.amount).toLocaleString('en-IN')} received via ${PAYMENT_METHODS.find(m => m.id === method)?.label}`
                }
              </div>

              {success.membershipId && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '0.8125rem', color: '#16a34a', marginBottom: '0.25rem' }}>Your Membership ID</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                    {success.membershipId}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '0.5rem' }}>
                    {success.planName} Plan · Valid for 1 year
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <Link to="/dashboard" style={{
                  padding: '0.625rem 1.25rem', background: '#2563eb', color: '#fff',
                  borderRadius: 10, textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem',
                }}>
                  Go to Dashboard
                </Link>
                <Link to="/my-bookings" style={{
                  padding: '0.625rem 1.25rem', background: '#f1f5f9', color: '#374151',
                  borderRadius: 10, textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem',
                }}>
                  My Bookings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-card">
          <h1 style={{ fontSize: '1.375rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>Complete Payment</h1>
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
            {state.type === 'rsa' ? `RSA ${state.planName} Plan — Annual Membership` : 'Service Payment'}
          </p>

          <div style={{ textAlign: 'center', margin: '1.25rem 0' }}>
            <div className="payment-amount">
              <span className="payment-currency">₹</span>
              {Number(state.amount).toLocaleString('en-IN')}
            </div>
          </div>

          <div style={{ height: 1, background: '#f1f5f9', margin: '1rem 0' }} />

          <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#374151', marginBottom: '0.75rem' }}>
            Select Payment Method
          </h3>

          <div className="payment-methods">
            {PAYMENT_METHODS.map((pm) => (
              <button
                key={pm.id}
                className={`payment-method-btn ${method === pm.id ? 'selected' : ''}`}
                onClick={() => setMethod(pm.id)}
                type="button"
              >
                <span style={{ fontSize: '1.25rem' }}>{pm.icon}</span>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600 }}>{pm.label}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400 }}>{pm.desc}</div>
                </div>
                {method === pm.id && (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {error && (
            <div className="auth-error-box" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {method === 'upi' && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8125rem', color: '#92400e' }}>
              Simulation mode: UPI payment will be marked as paid automatically.
            </div>
          )}

          <button
            className="payment-proceed-btn"
            onClick={handlePay}
            disabled={loading}
            type="button"
          >
            {loading ? 'Processing...' : method === 'cash' ? 'Confirm & Request Activation' : `Pay ₹${Number(state.amount).toLocaleString('en-IN')}`}
          </button>

          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.75rem' }}>
            🔒 Secured transaction · Your data is safe
          </p>
        </div>
      </div>
    </div>
  );
};

export default Payment;
